import { createSlice, createAsyncThunk, type PayloadAction } from '@reduxjs/toolkit';
import type { IUser } from 'models/user_model';
import {
  getUsers,
  createUser,
  editUser,
  deleteUser,
  changePassword,
} from 'repositories/usuario_repository';

interface UserState {
  users: IUser[];
  loading: boolean;
  error: string | null;
}

const initialState: UserState = {
  users: [],
  loading: false,
  error: null,
};

export const cargarUsuarios = createAsyncThunk<IUser[], { nombre?: string; email?: string } | undefined, { rejectValue: string }>(
  'user/cargarUsuarios',
  async (filters = {}, { rejectWithValue }) => {
    try {
      return ((await getUsers(filters.nombre, filters.email)) ?? []);
    } catch {
      return rejectWithValue('Error al cargar usuarios');
    }
  },
);

export const addUsuario = createAsyncThunk(
  'user/addUsuario',
  async (data: IUser, { rejectWithValue }) => {
    try {
      await createUser(data);
    } catch {
      return rejectWithValue('Error al crear usuario');
    }
  },
);

export const editUsuario = createAsyncThunk(
  'user/editUsuario',
  async ({ data, userId }: { data: Partial<IUser>; userId: string }, { rejectWithValue }) => {
    try {
      await editUser(data, userId);
    } catch {
      return rejectWithValue('Error al editar usuario');
    }
  },
);

export const deleteUsuario = createAsyncThunk(
  'user/deleteUsuario',
  async (id: string, { rejectWithValue }) => {
    try {
      await deleteUser(id);
    } catch {
      return rejectWithValue('Error al eliminar usuario');
    }
  },
);

export const cambiarContrasenia = createAsyncThunk(
  'user/cambiarContrasenia',
  async (newPass: string, { rejectWithValue }) => {
    try {
      return await changePassword(newPass);
    } catch {
      return rejectWithValue('Error al cambiar contraseña');
    }
  },
);

export const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    setUsuarios: (state, action: PayloadAction<IUser[]>) => {
      state.users = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(cargarUsuarios.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(cargarUsuarios.fulfilled, (state, action) => {
        state.loading = false;
        state.users = action.payload;
      })
      .addCase(cargarUsuarios.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { setUsuarios, clearError: clearUserError } = userSlice.actions;
export default userSlice.reducer;
